import { ApiError, RequestTimeout } from './errors.js';

/**
 * What a refused invitation means, and whether the token is worth keeping.
 *
 * The second half is the part that was wrong, and it cost somebody their
 * invitation. The screen used to discard the token on **every** failure,
 * reasoning that it was spent either way — accepted, or refused and therefore
 * useless. That is true of exactly one refusal.
 *
 * `resource.not_found` is the server's single answer for expired, revoked,
 * already accepted and addressed-to-somebody-else. Those are all "this
 * invitation is no good", and holding on to the token helps nobody.
 *
 * Everything else is about this attempt, not this invitation: a session that
 * expired mid-acceptance, a rate limit, a provider that does not put an e-mail
 * address in its token. The invitation is still live in the database — the
 * server refused before it ever looked at it — so throwing away the browser's
 * copy turns a two-minute configuration fix into "please send me a new link",
 * and the only copy of the token was in a CI log that has since scrolled away.
 */
export interface InvitationFailure {
  readonly message: string;
  /** False only when the invitation itself is no good. */
  readonly keepToken: boolean;
}

export function describeFailure(cause: unknown): InvitationFailure {
  if (cause instanceof RequestTimeout) {
    // Sent, and never answered: the invitation may well have been accepted on
    // the way. Trying again would then be refused as « déjà utilisée », which
    // reads as a failure on top of a success. The token is kept in case it
    // was not.
    return {
      message:
        'Le serveur n’a pas répondu à temps : l’invitation a peut-être été acceptée. Ouvrez le tableau de bord pour le vérifier avant de réessayer.',
      keepToken: true,
    };
  }

  if (!(cause instanceof ApiError)) {
    // A network failure, a gateway, a reload mid-request. Nothing was decided.
    return { message: 'La connexion a échoué. Réessayez dans un instant.', keepToken: true };
  }

  switch (cause.code) {
    case 'resource.not_found':
      return {
        message:
          'Cette invitation n’est plus valide : elle a peut-être expiré, déjà été utilisée, ou été envoyée à une autre adresse que celle avec laquelle vous vous êtes connecté.',
        keepToken: false,
      };
    case 'authz.denied':
      // Almost always the provider, not the person. Saying "ce compte ne peut
      // pas" blamed the one party who had done nothing wrong, and gave whoever
      // had to fix it nothing to go on.
      return {
        message:
          'Votre fournisseur d’identité n’a pas transmis votre adresse e-mail, et l’invitation ne peut pas être vérifiée sans elle. Ce n’est pas votre compte qui est en cause : c’est une configuration à corriger de notre côté.',
        keepToken: true,
      };
    case 'authn.required':
      return {
        message: 'Votre session a expiré pendant l’acceptation. Reconnectez-vous et réessayez.',
        keepToken: true,
      };
    case 'rate_limited':
      return {
        message: 'Trop de tentatives. Patientez un instant avant de réessayer.',
        keepToken: true,
      };
    default:
      return {
        message:
          cause.requestId === null
            ? 'Une erreur est survenue. Réessayez dans un instant.'
            : `Une erreur est survenue (référence ${cause.requestId}).`,
        keepToken: true,
      };
  }
}
